import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, Eyebrow, ArrowLink } from "@/components/Reveal";

const STADIUM_IMG =
  "https://images.unsplash.com/photo-1647849402208-01775e9c9fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxzdGFkaXVtJTIwZXh0ZXJpb3IlMjBzdHJ1Y3R1cmV8ZW58MHx8fHwxNzkxODMzMDI2fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = {
  "strategic-advisory": {
    eyebrow: "Expertise — 01",
    title: "Strategic Advisory & M&A",
    intro: [
      "PJG advises shareholders, investors and business owners on acquisitions, disposals, ownership transitions, mergers and strategic transactions.",
      "Our work is deliberately selective: a small number of significant mandates, each led personally and pursued with discretion. We combine rigorous assessment of the underlying business or asset with a clear understanding of the commercial and personal priorities behind every ownership decision.",
    ],
    focus: [
      "Sell-side advisory & business disposals",
      "Buy-side acquisitions & target origination",
      "Ownership transitions & succession",
      "Mergers & strategic combinations",
      "Board & shareholder counsel",
      "Preparation for sale & strategic positioning",
    ],
  },
  "private-capital": {
    eyebrow: "Expertise — 02",
    title: "Private Capital & Institutional Advisory",
    intro: [
      "We work with private equity sponsors, investment managers, family offices and institutional investors on capital strategies, partnerships, acquisitions and potential investment mandates.",
      "Operating across private capital markets, we help originate opportunities, connect institutional and private capital, and structure partnerships across private businesses, real assets and alternative investments — within applicable regulatory permissions.",
    ],
    focus: [
      "Capital strategy & partnership structuring",
      "Private placements & investment origination",
      "Joint ventures & co-investment opportunities",
      "Acquisition & investment mandates",
      "Alternative investment opportunities",
      "Relationships with asset managers & investment funds",
    ],
  },
  "family-office-advisory": {
    eyebrow: "Expertise — 03",
    title: "Family Office Advisory",
    intro: [
      "We provide independent, long-term strategic support to wealthy families — combining the personal attention of a multi-family office with the transaction capabilities of an institutional advisory house.",
      "Our role is that of trusted counsel: originating direct investments, shaping acquisition strategy, reviewing existing assets and coordinating seamlessly with a family's specialist advisers across significant private capital decisions.",
    ],
    focus: [
      "Direct investment origination",
      "Acquisition strategy & execution support",
      "Asset & portfolio reviews",
      "Coordination with specialist advisers",
      "Long-term private capital strategy",
      "Significant private asset transactions",
    ],
  },
  "sports-investments": {
    eyebrow: "Expertise — 04",
    title: "Sports Investments",
    image: STADIUM_IMG,
    imageAlt: "World-class professional football stadium architecture",
    intro: [
      "Professional sport has become an increasingly significant area of interest for private capital, institutional investors and international family offices. PJG's sports investment advisory practice encompasses ownership transactions, strategic investments and capital partnerships across professional sport — with a particular focus on professional football.",
      "We act for family offices, private capital firms and prospective club owners, originating opportunities and advising through every stage of a sports transaction, from initial approach to completion.",
    ],
    focus: [
      "Professional football club acquisitions & disposals",
      "Minority & majority ownership investments",
      "Sports-related mergers & acquisitions",
      "Institutional investment into sports businesses",
      "Strategic ownership partnerships",
      "Sports infrastructure & commercial assets",
      "Investment origination for family offices & prospective club owners",
    ],
  },
};

export default function ServicePage({ serviceId }) {
  const s = SERVICES[serviceId];
  if (!s) return null;

  return (
    <main className="bg-[#F6F4F0] text-[#1F1E1B] antialiased">
      <Navbar />

      <section data-testid="service-hero" className="pt-40 lg:pt-52 pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>{s.eyebrow}</Eyebrow>
            <h1
              data-testid="service-title"
              className="mt-8 font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-[#1F1E1B] leading-[1.05] max-w-5xl"
            >
              {s.title}
            </h1>
          </Reveal>
        </div>
      </section>

      {s.image && (
        <section className="pb-16 lg:pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal delay={0.1}>
              <div className="relative aspect-[21/9] overflow-hidden border border-[#E3E0D8]">
                <img
                  src={s.image}
                  alt={s.imageAlt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale contrast-110 brightness-[0.92]"
                />
                <span className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#F6F4F0]/80" aria-hidden="true" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="pb-24 lg:pb-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            {s.intro.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.07}>
                <p
                  className={`text-base sm:text-lg font-light leading-relaxed ${
                    i === 0 ? "text-[#1F1E1B]/85" : "text-[#6E6A61]"
                  } ${i > 0 ? "mt-6" : ""}`}
                >
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="mt-12">
                <ArrowLink to="/contact" testid="service-enquiry-link">
                  Discuss a Mandate
                </ArrowLink>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#6E6A61] mb-2">
                Areas of Focus
              </p>
            </Reveal>
            <div className="border-t border-[#E3E0D8]">
              {s.focus.map((f, i) => (
                <Reveal key={f} delay={0.15 + i * 0.04}>
                  <div
                    data-testid={`service-focus-${i}`}
                    className="group flex items-baseline gap-6 py-4 border-b border-[#E3E0D8] transition-colors duration-500 hover:bg-[#FFFFFF]"
                  >
                    <span className="font-sans text-[11px] tracking-[0.24em] text-[#6E6A61] group-hover:text-[#1F1E1B] transition-colors duration-500">
                      0{i + 1}
                    </span>
                    <span className="text-sm sm:text-base font-light text-[#1F1E1B]/85">{f}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
